import {AfterViewInit, Component, ElementRef, ViewChild} from '@angular/core';

import { HelpService } from '@app/common/services/help.service';
import { SearchService } from '@app/search/services/search.service';
import { SearchResultModel } from '@app/search/components/model/search-result.model';
import { SearchFilterModel } from '@app/search/components/model/search-filter.model';
import { UICONSTANTS } from '@app/common/services/config.service';
import { ListModel } from '@app/common/models/list.model';

@Component({
  templateUrl: './search.component.html',
  styleUrls: ['search.component.scss']
})
export class SearchComponent implements AfterViewInit {

  helpIds = HelpService.HELP_IDS;
  query = '';

  categories = [
    { id: 'all', name: 'Vše', index: undefined, list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0 },
    { id: 'project', name: 'Akce', index: 'project', list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0, icon: 'fa fa-folder' },
    { id: 'case', name: 'Případy', index: 'case', list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0, icon: 'fa fa-file' },
    { id: 'subject', name: 'Vlastníci', index: 'subject', list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0, icon: 'fa fa-user' },
    { id: 'parcel', name: 'Parcely', index: 'parcel', list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0, icon: 'fa fa-toggle-up' },
    { id: 'title', name: 'LV', index: 'title', list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0, icon: 'fa fa-users' },
    { id: 'gp', name: 'GP', index: 'geometric_plan', list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0, icon: 'fa fa-object-ungroup' },
    { id: 'zp', name: 'ZP', index: 'opinion', list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0, icon: 'fa fa-book' },
    { id: 'component', name: 'Komponenty', index: 'component', list: undefined, active: false, cachedCount: 0, lastQuery: '', lastQueryTime: 0, icon: 'fa fa-file-o' }
  ];

  list: ListModel<SearchResultModel>;
  itemsPerPage = UICONSTANTS.itemsPerPage10;
  dataPaging: any = { itemsPerPage: { id: 10, name: 10 }};
  onListChangedPagination: Function;
  onListChangedItemCounter: Function;
  onListChangedPagesCounter: Function;

  filters: SearchFilterModel[] = [];

  private readonly CACHE_TTL_MS = 5 * 60 * 1000;

  @ViewChild('searchInput')
  private searchInputEl!: ElementRef<HTMLInputElement>;

  constructor(
    private searchService: SearchService,
  ) {
    this.onSearch = this.onSearch.bind(this);
    this.onAddFilter = this.onAddFilter.bind(this);
  }

  ngAfterViewInit() {
    this.searchInputEl.nativeElement.focus();
  }

  onSearch(): Promise<any> | void {
    const trimmed = (this.query || '').trim();
    if (!trimmed || trimmed.length < 1) { return; }
    this.query = trimmed;
    this.categories.forEach(c => { c.lastQuery = ''; c.lastQueryTime = 0; });
    const category = this.categories.find(c => c.active === true) || this.categories.find(c => c.id === 'all');
    this.onChangeCategory(category);
  }

  refreshAllCounts() {
    this.searchService.findByQuery(this.filters, this.query, 0, 0, undefined).then(result => {
      if (result) {
        const allCat = this.categories.find(c => c.id === 'all');
        if (result.stats) {
          this.categories.forEach(c => {
            if (c.index !== undefined) {
              c.cachedCount = result.stats[c.index] || 0;
            }
          });
          if (allCat) {
            allCat.cachedCount = Object.values(result.stats).reduce((sum: number, v: number) => sum + v, 0);
          }
        } else if (allCat) {
          allCat.cachedCount = result.totalCount;
        }
      }
    });
  }

  onChangeCategory(category: any) {
    this.categories.forEach(c => c.active = false);
    category.active = true;

    if (!category.list) {
      category.list = {
        loading: true,
        itemCount: 0,
        list: []
      };
    }
    category.list.filter = { offset: 0, limit: this.dataPaging.itemsPerPage.id };
    this.list = category.list;
    if (!this.query || !this.query.trim().length) {
      this.list.loading = false;
      this.list.list = [];
      this.list.itemCount = 0;
      this.categories.forEach(c => { c.cachedCount = 0; });
      setTimeout(() => this.onListChanged());
    } else if (!this.isCacheValid(category)) {
      this.reloadList();
    } else {
      this.list.loading = false;
      setTimeout(() => this.onListChanged());
    }
  }

  reloadList() {
    this.list.loading = true;
    const activeCategory = this.categories.find(c => c.active);

    if (activeCategory.id !== 'all') {
      this.refreshAllCounts();
    }

    return this.searchService.findByQuery(
      this.filters,
      this.query,
      this.dataPaging.itemsPerPage.id,
      this.list.filter.offset,
      activeCategory.index
    ).then(result => {
      if (result) {
        this.list.loading = false;
        this.list.list = result.results;
        this.list.itemCount = result.totalCount;
        this.list.stats = result.stats;

        activeCategory.cachedCount = activeCategory.index
          ? ((result.stats && result.stats[activeCategory.index]) || 0)
          : (result.stats ? Object.values(result.stats).reduce((sum: number, v: number) => sum + v, 0) : result.totalCount);
        activeCategory.lastQuery = this.query;
        activeCategory.lastQueryTime = Date.now();

        if (activeCategory.id === 'all') {
          this.categories.forEach(c => {
            if (c.index !== undefined) {
              c.cachedCount = (result.stats && result.stats[c.index]) || 0;
            }
          });
        }

        this.onListChanged();
      }
    })
  }

  onAddFilter(filter: SearchFilterModel) {
    this.filters.push(filter);
    this.query = '';
  }

  onClear() {
    this.query = '';
    this.searchInputEl.nativeElement.focus();
  }

  onRemoveFilter(filter: SearchFilterModel) {
    this.filters = this.filters.filter(f => f.name !== filter.name);
  }

  onPageItemsChanged() {
    this.list.filter.offset = 0;
    this.list.filter.limit = this.dataPaging.itemsPerPage.id;
    this.reloadList();
  }

  onPageChanged(event) {
    this.list.filter.offset = (event - 1) * this.dataPaging.itemsPerPage.id;
    this.reloadList();
  }

  pagesCounterCallbackRegister(event) {
    this.onListChangedPagesCounter = event.onListChanged;
  }

  itemCounterCallbackRegister(event) {
    this.onListChangedItemCounter = event.onListChanged;
  }

  paginationCallbackRegister(event) {
    this.onListChangedPagination = event.onListChanged;
  }

  onListChanged() {
    if (this.onListChangedPagination) {
      this.onListChangedPagination();
    }
    if (this.onListChangedItemCounter) {
      this.onListChangedItemCounter();
    }
    if (this.onListChangedPagesCounter) {
      this.onListChangedPagesCounter();
    }
  }

  private isCacheValid(category: any): boolean {
    return category.lastQuery === this.query
      && category.lastQueryTime > 0
      && (Date.now() - category.lastQueryTime) < this.CACHE_TTL_MS;
  }
}
